import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Bell, CheckCheck, DollarSign, Loader2 } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

const POLL_INTERVAL = 60_000; // 1 minuto

const tipoIcon = {
  pagamento_recebido: DollarSign,
};
const tipoColor = {
  pagamento_recebido: "bg-emerald-100 text-emerald-700",
};

function formatRelativeTime(iso) {
  try {
    const d = new Date(iso);
    const diff = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diff < 60) return "agora";
    if (diff < 3600) return `${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
    return `${Math.floor(diff / 86400)} d`;
  } catch {
    return "";
  }
}

export default function NotificacoesBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const refreshCount = useCallback(async () => {
    try {
      const r = await api.get("/notificacoes-internas/nao-lidas/count");
      setCount(r.data?.count || 0);
    } catch {
      // silently fail (auth ainda não pronto, etc)
    }
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/notificacoes-internas?limit=20");
      setItems(r.data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [refreshCount]);

  useEffect(() => {
    if (open) loadItems();
  }, [open, loadItems]);

  const handleMarcarLida = async (notif) => {
    try {
      if (!notif.lida) {
        await api.post(`/notificacoes-internas/${notif.id}/marcar-lida`);
      }
      // navegação contextual
      if (notif.tipo === "pagamento_recebido") {
        navigate("/mensalidades");
      }
      setOpen(false);
      refreshCount();
    } catch {
      // silent
    }
  };

  const handleMarcarTodasLidas = async () => {
    try {
      await api.post("/notificacoes-internas/marcar-todas-lidas");
      refreshCount();
      loadItems();
    } catch {
      // silent
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="bell-notificacoes-trigger"
          aria-label="Notificações"
        >
          <Bell className="w-5 h-5" />
          {count > 0 && (
            <span
              className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white"
              data-testid="bell-badge-count"
            >
              {count > 99 ? "99+" : count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-0"
        data-testid="bell-popover"
      >
        <div className="flex items-center justify-between p-3 border-b">
          <div className="font-semibold text-sm">Notificações</div>
          {count > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={handleMarcarTodasLidas}
              data-testid="bell-marcar-todas-lidas"
            >
              <CheckCheck className="w-3.5 h-3.5 mr-1" />
              Marcar todas
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-96">
          {loading ? (
            <div className="flex items-center justify-center p-6 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Carregando...
            </div>
          ) : items.length === 0 ? (
            <div
              className="text-center p-6 text-sm text-gray-500"
              data-testid="bell-empty"
            >
              <Bell className="w-8 h-8 mx-auto text-gray-300 mb-2" />
              Nenhuma notificação ainda.
            </div>
          ) : (
            <div className="divide-y">
              {items.map((notif) => {
                const Icon = tipoIcon[notif.tipo] || Bell;
                const colorCls = tipoColor[notif.tipo] || "bg-gray-100 text-gray-700";
                return (
                  <button
                    key={notif.id}
                    onClick={() => handleMarcarLida(notif)}
                    className={`w-full text-left p-3 hover:bg-gray-50 transition-colors flex items-start gap-3 ${
                      !notif.lida ? "bg-blue-50/50" : ""
                    }`}
                    data-testid={`bell-item-${notif.id}`}
                  >
                    <div
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${colorCls}`}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm truncate">
                          {notif.titulo}
                        </span>
                        {!notif.lida && (
                          <span className="h-2 w-2 rounded-full bg-blue-500 shrink-0" />
                        )}
                      </div>
                      <div className="text-xs text-gray-600 line-clamp-2 mt-0.5">
                        {notif.mensagem}
                      </div>
                      <div className="text-[10px] text-gray-400 mt-1">
                        {formatRelativeTime(notif.created_at)}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
